/**
 * Eine Apartmentzeile der Belegung: sticky Beschriftung, Tageszellen und die Balken in einer oder
 * zwei Bahnen. Ziehen legt den Balken auf eine andere Zeile; eine Zeile, die ihn nicht aufnehmen
 * kann, zeigt einen roten Ring und nennt den Grund beim Loslassen.
 */
import { useDroppable } from '@dnd-kit/core';
import type { GuestAccess } from './bookingDetailsModel';
import { cn } from '@/shared/lib/cn';
import type { CalendarPropertyRow as RowData } from '@/shared/api';
import { BookingPill } from './BookingPill';
import { planColumn } from './calendarBarText';
import { CalendarDayCell, type CalendarCellKind } from './CalendarDayCells';
import { calendarCellState } from './calendarFamily';
import { isWeekend, monthBreakIndices, type BookingSpan } from './calendarHelpers';
import { CalendarPlanMark } from './CalendarLegend';
import type { CalendarReassignmentDragData } from './calendarReassignment';
import { CalendarRowSidebar } from './CalendarRowSidebar';
import { rowIsDoubleBooked, type CalendarRowModel } from './calendarRowModel';

export interface RowGeometry {
  sidebarWidth: number;
  cellMin: number;
  rowHeight: number;
  compact: boolean;
  /** Was der Balken trägt: alles, nur den Nachnamen (Handy, 7 Tage) oder nichts (14 Tage). */
  labels: 'full' | 'short' | 'none';
}

interface Props {
  row: RowData;
  model: CalendarRowModel;
  isRoom?: boolean;
  address?: string;
  geometry: RowGeometry;
  onFreeDay?: (row: RowData, date: string, anchor: HTMLElement) => void;
  /** Gastdaten wie der Server: Name und Telefon mit Kontakt-Recht, E-Mail mit vollen Gastdaten. */
  guestAccess: GuestAccess;
  operations: boolean;
  today: string;
  canReassign: boolean;
  onReassignRequested?: (span: BookingSpan, sourcePropertyId: string) => void;
  /** Grund, aus dem diese Zeile die gerade gezogene Buchung nicht aufnimmt. */
  dropRefusal?: (drag: CalendarReassignmentDragData) => string | null;
}

const REASSIGNABLE = ['pending', 'confirmed', 'checkedin'];

export function CalendarPropertyRow({ row, model, isRoom = false, geometry, ...props }: Props) {
  // Ein pausiertes Apartment bleibt sichtbar – seine Buchungen existieren weiter –, aber eine freie
  // Nacht ist kein Angebot mehr: der Server weist eine neue Buchung ab.
  const paused = row.status === 'Paused';
  const dates = row.cells.map((cell) => cell.date);
  const breaks = monthBreakIndices(dates);
  const drop = useDroppable({
    id: `property-${row.propertyId}`,
    disabled: !props.canReassign || row.status !== 'Active',
    data: { targetPropertyId: row.propertyId },
  });
  const dragged = drop.active?.data.current as CalendarReassignmentDragData | undefined;
  const refused = drop.isOver && dragged ? props.dropRefusal?.(dragged) : null;
  const kindOf = (index: number): CalendarCellKind => {
    if (model.doubleNights.has(index)) return 'double';
    const state = calendarCellState(row.cells[index]!);
    return state === 'doubleBooked' ? 'double' : state;
  };

  return (
    <div
      ref={drop.setNodeRef}
      className={cn(
        'border-card-alt relative grid border-b transition-shadow',
        drop.isOver &&
          (refused ? 'ring-sealwax ring-2 ring-inset' : 'ring-forest ring-2 ring-inset'),
      )}
      style={{
        gridTemplateColumns: `${geometry.sidebarWidth}px repeat(${dates.length}, minmax(${geometry.cellMin}px, 1fr))`,
        gridTemplateRows: `${geometry.rowHeight}px`,
      }}
    >
      <CalendarRowSidebar
        row={row}
        address={props.address}
        occupancyPct={model.occupancyPct}
        paused={paused}
        isRoom={isRoom}
        doubleBooked={rowIsDoubleBooked(model)}
        compact={geometry.compact}
      />
      {row.cells.map((cell, index) => (
        <CalendarDayCell
          key={cell.date}
          kind={kindOf(index)}
          date={cell.date}
          weekend={isWeekend(cell.date)}
          isMonthBreak={breaks.has(index)}
          isToday={cell.date === props.today}
          gridPos={{ gridColumn: index + 2, gridRow: 1 }}
          onBook={
            props.onFreeDay && !paused
              ? (anchor) => props.onFreeDay?.(row, cell.date, anchor)
              : undefined
          }
        />
      ))}
      {model.bars.map(({ span, lane, dimmed }) => {
        const eligible = props.canReassign && REASSIGNABLE.includes(span.bookingStatus);
        return (
          <BookingPill
            key={span.bookingId}
            span={span}
            propertyId={row.propertyId}
            propertyName={row.propertyName}
            gridStart={span.startIndex + 2}
            currency={row.currency ?? 'EUR'}
            dimmed={dimmed}
            lanes={model.lanes}
            lane={lane}
            compact={geometry.compact}
            labels={geometry.labels}
            guestAccess={props.guestAccess}
            operations={props.operations}
            today={props.today}
            canReassign={eligible}
            onReassignRequested={
              eligible && props.onReassignRequested
                ? () => props.onReassignRequested?.(span, row.propertyId)
                : undefined
            }
          />
        );
      })}
      {model.bars.map(({ span }) => {
        const column = planColumn(span, dates[0] ?? '', dates.length);
        return column === null ? null : (
          <CalendarPlanMark key={`plan-${span.bookingId}`} column={column} />
        );
      })}
    </div>
  );
}
